import * as zod from 'zod';
import { useAtom } from 'jotai/react';
import { persistedAtom } from '../utils/persistedAtom';
import { StatusFilter, type CategoryFilter } from './filterTypes';

/**
 * Per-user Planning session state persisted in localStorage so that navigating
 * away from Planning and back restores the last view instead of resetting
 * (SEABER-6666). Cleared on logout via lib/clearPersistedSession.ts.
 *
 * Keys are exported so the logout cleaner has a single source of truth.
 */
export const ORDER_STATUS_FILTER_KEY = 'planning_order_status_filter';
export const VESSEL_FILTER_LABELS_KEY = 'planning_vessel_filter_labels';
export const VESSEL_FILTER_IDS_KEY = 'planning_vessel_filter_ids';
export const VESSEL_CATEGORY_KEY = 'planning_vessel_category';
export const TIMELINE_WINDOW_KEY = 'planning_timeline_window';

export const PLANNING_SESSION_KEYS = [
  ORDER_STATUS_FILTER_KEY,
  VESSEL_FILTER_LABELS_KEY,
  VESSEL_FILTER_IDS_KEY,
  VESSEL_CATEGORY_KEY,
  TIMELINE_WINDOW_KEY,
] as const;

/* Order-list status filter tab */
const orderStatusFilterAtom = persistedAtom({
  key: ORDER_STATUS_FILTER_KEY,
  schema: zod.nativeEnum(StatusFilter),
  defaultValue: StatusFilter.OPEN,
});

export const useOrderStatusFilterState = () => useAtom(orderStatusFilterAtom);

/* Vessel-group / schedule filter — kept as three atoms mirroring the original
 * useState fields so each updates independently without object-mutation hazards. */
const categoryFilterSchema: zod.ZodType<CategoryFilter> = zod.enum([
  'all',
  'shortlist',
  'estimates',
  'updated',
  'open',
  'reserved',
  'modified',
  'tc',
  'coa',
  'spot',
]);

const vesselFilterLabelsAtom = persistedAtom({
  key: VESSEL_FILTER_LABELS_KEY,
  schema: zod.array(zod.string()),
  defaultValue: ['All'],
});

export const useVesselFilterLabelsState = () => useAtom(vesselFilterLabelsAtom);

const vesselFilterIdsAtom = persistedAtom({
  key: VESSEL_FILTER_IDS_KEY,
  schema: zod.array(zod.number()),
  defaultValue: [0],
});

export const useVesselFilterIdsState = () => useAtom(vesselFilterIdsAtom);

const vesselCategoryAtom = persistedAtom({
  key: VESSEL_CATEGORY_KEY,
  schema: categoryFilterSchema,
  defaultValue: 'all',
});

export const useVesselCategoryState = () => useAtom(vesselCategoryAtom);

/* Timeline visible window: start = scroll position, (end - start) = zoom span.
 * The atom itself is defined in TimelinePreferencesContext (where the moment-based
 * default window lives) so it stays a module-level singleton shared across both
 * provider mounts; this module only owns the key + schema. */
export const timelineWindowSchema = zod.object({
  start: zod.number(),
  end: zod.number(),
});

export type TimelineWindow = zod.infer<typeof timelineWindowSchema>;
